import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { faltaDeAcceso } from "../lib/acceso";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useEstadoCfdi, claveCfdi, T as T_CFDI } from "../lib/cfdi";
import EmisorCfdiPanel from "../components/cfdi/EmisorCfdiPanel";

// ═══════════════════════════════════════════════════════════════════════════════
// FACTURACIÓN (CL.3, 1 Oct 2026) — los pagos de la clínica y su CFDI.
//
// 🔴 TODA LA LÓGICA ESTÁ EN AGENCY. Lista, ventana de facturación, timbrado, descarga y datos
//    fiscales son las mismas funciones que usa surit.ai en /dashboard/pagos. El backend de
//    clínicas sólo hace de proxy (`/api/billing/*`) con la clínica del JWT.
//
// 🔴 EL FRONTEND NO CALCULA NADA DE LA VENTANA. `puede_facturar` decide si hay botón;
//    `limite_facturacion` sólo se FORMATEA para pintar la fecha. Ni aritmética de calendario ni
//    «último día del mes» aquí: el servidor decide el hecho y la pantalla lo copia.
//
// 🔴 COPY: los textos son los de surit.ai (`app/(dashboard)/pagos/page.tsx`), literales. Un texto
//    nuevo de cara al cliente pasa por Jesús antes de entrar aquí.
// ═══════════════════════════════════════════════════════════════════════════════

const REGIMENES = [
  { code: "601", label: "601 — General de Ley Personas Morales" },
  { code: "603", label: "603 — Personas Morales con Fines no Lucrativos" },
  { code: "606", label: "606 — Arrendamiento" },
  { code: "612", label: "612 — Personas Físicas con Actividades Empresariales y Profesionales" },
  { code: "616", label: "616 — Sin obligaciones fiscales" },
  { code: "621", label: "621 — Incorporación Fiscal" },
  { code: "625", label: "625 — Régimen de las Actividades Empresariales con ingresos a través de Plataformas" },
  { code: "626", label: "626 — Régimen Simplificado de Confianza (RESICO)" },
];

const USOS_CFDI = [
  { code: "G01", label: "G01 — Adquisición de bienes" },
  { code: "G03", label: "G03 — Gastos en general" },
  { code: "D01", label: "D01 — Honorarios médicos y dentales" },
  { code: "S01", label: "S01 — Sin efectos fiscales" },
];

// Siempre en hora de México, como surit.ai: `paid_at` llega en UTC y `limite_facturacion` en
// México (-06:00). Sin fijar el huso, un pago del 31 a las 19:00 se pintaría «1 sep» al lado de
// «puedes facturar hasta el 31 de agosto».
const TZ = "America/Mexico_City";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    timeZone: TZ, day: "2-digit", month: "short", year: "numeric",
  });
}

function yearInMx(d) {
  return new Intl.DateTimeFormat("es-MX", { timeZone: TZ, year: "numeric" }).format(d);
}

// Formato de la fecha límite ("31 de agosto"). Es FORMATO, no estado: quien decide si la ventana
// sigue abierta es `puede_facturar`.
function fmtLimite(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mismoAno = yearInMx(d) === yearInMx(new Date());
  return d.toLocaleDateString("es-MX", {
    timeZone: TZ, day: "numeric", month: "long",
    ...(mismoAno ? {} : { year: "numeric" }),
  });
}

function fmtMoney(n) {
  return `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`;
}

// El backend devuelve `{detail: str, code}` top-level (DT.60/DT.268).
function datosDelError(e) {
  const d = e?.response?.data || {};
  return { detail: formatApiError(d.detail), code: d.code || null };
}

async function descargar(url, filename) {
  // `url` es relativa a /api (`/billing/invoices/<id>/pdf`); el JWT lo pone el interceptor.
  const res = await api.get(url, { responseType: "blob" });
  const objUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

const inputCls =
  "mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-electric";

function DatosFiscalesModal({ inicial, onSaved, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const rfcRef = useRef(null);
  const razonRef = useRef(null);
  const regimenRef = useRef(null);
  const usoRef = useRef(null);
  const cpRef = useRef(null);

  const handleSave = async () => {
    setError(null);
    const rfc = (rfcRef.current?.value || "").trim().toUpperCase();
    const razon_social = (razonRef.current?.value || "").trim();
    const regimen_fiscal = regimenRef.current?.value || "";
    const uso_cfdi = usoRef.current?.value || "";
    const cp = (cpRef.current?.value || "").trim();

    if (!rfc || !razon_social || !regimen_fiscal || !uso_cfdi || !cp) {
      setError("Todos los campos son obligatorios");
      return;
    }
    if (!/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/.test(rfc)) {
      setError("RFC inválido. Formato: 3-4 letras + 6 dígitos + 3 alfanuméricos");
      return;
    }
    if (!/^\d{5}$/.test(cp)) {
      setError("El código postal debe tener 5 dígitos");
      return;
    }

    setSaving(true);
    try {
      await api.put("/billing/fiscal", { rfc, razon_social, regimen_fiscal, uso_cfdi, cp });
      onSaved();
    } catch (e) {
      // La validación del servidor (agency, `TenantFiscalDataUpdate`) llega aplanada a cadena.
      setError(e?.response ? datosDelError(e).detail : "Error al guardar datos fiscales");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" data-testid="fiscal-modal">
        <h2 className="mb-1 text-lg font-bold text-slate-900">Datos fiscales</h2>
        <p className="mb-5 text-sm text-slate-500">
          Necesitamos tus datos fiscales para emitir tu CFDI 4.0.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="rfc" className="text-sm font-medium text-slate-700">RFC *</label>
            <input id="rfc" ref={rfcRef} placeholder="XAXX010101000" maxLength={13}
              defaultValue={inicial?.rfc || ""} className={`${inputCls} uppercase`} />
          </div>
          <div>
            <label htmlFor="razon" className="text-sm font-medium text-slate-700">Razón social *</label>
            <input id="razon" ref={razonRef} placeholder="MI EMPRESA S.A. DE C.V."
              defaultValue={inicial?.razon_social || ""} className={inputCls} />
          </div>
          <div>
            <label htmlFor="regimen" className="text-sm font-medium text-slate-700">Régimen fiscal *</label>
            <select id="regimen" ref={regimenRef} defaultValue={inicial?.regimen_fiscal || ""} className={inputCls}>
              <option value="">Selecciona régimen</option>
              {REGIMENES.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="uso" className="text-sm font-medium text-slate-700">Uso de CFDI *</label>
            <select id="uso" ref={usoRef} defaultValue={inicial?.uso_cfdi || "G03"} className={inputCls}>
              {USOS_CFDI.map((u) => (
                <option key={u.code} value={u.code}>{u.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="cp" className="text-sm font-medium text-slate-700">Código postal del receptor *</label>
            <input id="cp" ref={cpRef} placeholder="29000" maxLength={5}
              defaultValue={inicial?.cp || ""} className={inputCls} />
          </div>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="fiscal-error">{error}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} disabled={saving}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="rounded-lg bg-brand-electric px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Facturacion() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // 🔴 Un fallo al cargar NO se pinta como «Aún no tienes pagos»: un cero por avería y un cero
  //    real son cosas distintas (DT.233). Se muestra el motivo que da el servidor.
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    ["ok", "cancelado"].includes(new URLSearchParams(window.location.search).get("paquete_cfdi")) ? "pacientes" : "pagos");
  const [requesting, setRequesting] = useState(null);
  const [requestResult, setRequestResult] = useState({});
  const [fiscalModal, setFiscalModal] = useState(null); // null | {inicial}
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [downloadMsg, setDownloadMsg] = useState(null);
  // CL.4 C: la pestaña de facturas a pacientes sólo existe si el backend la ofrece (bandera
  // encendida para la clínica). Con un 404 no se pinta NADA y la página queda como hoy.
  const { activeClinic, user } = useAuth() || {};
  const cfdi = useEstadoCfdi(claveCfdi(activeClinic, user));
  const pestanas = cfdi.disponible || cfdi.error ? ["pagos", "facturas", "pacientes"] : ["pagos", "facturas"];

  const loadInvoices = async () => {
    try {
      const { data } = await api.get("/billing/invoices");
      setInvoices(data.invoices || []);
      setLoadError(null);
    } catch (e) {
      const acceso = faltaDeAcceso(e);
      setLoadError(acceso ? acceso.detail : datosDelError(e).detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInvoices(); }, []);

  const abrirFiscal = async (invoiceId) => {
    let inicial = null;
    try {
      const { data } = await api.get("/billing/fiscal");
      inicial = data || null;
    } catch {
      inicial = null;
    }
    setPendingInvoiceId(invoiceId || null);
    setFiscalModal({ inicial });
  };

  const doRequestInvoice = async (invoiceId) => {
    setRequesting(invoiceId);
    try {
      const { data } = await api.post(`/billing/invoices/${encodeURIComponent(invoiceId)}/request`);
      setRequestResult((prev) => ({
        ...prev,
        [invoiceId]: { success: true, warning: data?.warning || undefined },
      }));
      await loadInvoices();
    } catch (e) {
      const { detail, code } = datosDelError(e);
      if (code === "no_fiscal_data") {
        setRequesting(null);
        await abrirFiscal(invoiceId);
        return;
      }
      setRequestResult((prev) => ({
        ...prev,
        [invoiceId]: {
          success: false,
          error: detail || "Error al solicitar factura",
          canReload: code === "INVOICE_WINDOW_CLOSED",
        },
      }));
    } finally {
      setRequesting(null);
    }
  };

  const handleRequestInvoice = async (invoiceId) => {
    setRequesting(invoiceId);
    setRequestResult((prev) => {
      const next = { ...prev };
      delete next[invoiceId];
      return next;
    });
    let fiscal = null;
    try {
      const { data } = await api.get("/billing/fiscal");
      fiscal = data;
    } catch {
      fiscal = null;
    }
    if (!fiscal || !fiscal.rfc) {
      setRequesting(null);
      setPendingInvoiceId(invoiceId);
      setFiscalModal({ inicial: fiscal });
      return;
    }
    await doRequestInvoice(invoiceId);
  };

  const onFiscalSaved = async () => {
    setFiscalModal(null);
    if (pendingInvoiceId) {
      const id = pendingInvoiceId;
      setPendingInvoiceId(null);
      await doRequestInvoice(id);
    }
  };

  const handleDownload = async (url, type, inv) => {
    setDownloadMsg("Descargando…");
    try {
      const label = inv?.cfdi_uuid ? inv.cfdi_uuid.slice(0, 8) : String(inv?.id || "").slice(0, 8);
      await descargar(url, `factura-${label}.${type}`);
      setDownloadMsg(null);
    } catch {
      setDownloadMsg("Error al descargar. Intenta de nuevo.");
      setTimeout(() => setDownloadMsg(null), 3000);
    }
  };

  const withCfdi = invoices.filter((i) => i.has_cfdi);

  if (loading) {
    return <div className="p-6 lg:p-8 text-slate-500">Cargando pagos…</div>;
  }

  const btnOutline =
    "rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-4xl mx-auto w-full" data-testid="facturacion-page">
      <PageHeader
        title="Mis pagos y facturas"
        subtitle="Consulta tus pagos y solicita tu factura CFDI cuando la necesites."
        actions={
          <button onClick={() => abrirFiscal(null)} className={btnOutline} data-testid="abrir-datos-fiscales">
            Datos fiscales
          </button>
        }
      />

      {/* Fija y SIEMPRE visible: es la regla del servicio, no el estado de una fila. */}
      <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-sm text-slate-600">
          La facturación está disponible solo durante el mes en curso del pago.
        </p>
      </div>

      {loadError ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800" data-testid="facturacion-error">
          {loadError}
        </div>
      ) : (
        <>
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1 w-fit">
            {pestanas.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-lg px-5 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab === "pagos" ? "Mis pagos" : tab === "facturas" ? "Mis facturas" : T_CFDI.tab}
              </button>
            ))}
          </div>

          {downloadMsg && <p className="text-sm text-slate-500">{downloadMsg}</p>}

          {activeTab === "pacientes" && (cfdi.disponible || cfdi.error) && (
            <EmisorCfdiPanel estado={cfdi.estado} error={cfdi.error} onCambio={cfdi.recargar} />
          )}

          {activeTab === "pagos" && (
            <div className="space-y-3">
              {invoices.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
                  <p className="text-slate-500">Aún no tienes pagos registrados.</p>
                  <p className="mt-1 text-sm text-slate-400">
                    Los pagos aparecerán aquí una vez que contrates un servicio.
                  </p>
                </div>
              ) : (
                invoices.map((inv) => {
                  const result = requestResult[inv.id];
                  const pendienteDeFacturar = !inv.has_cfdi && inv.status === "paid";
                  const limite = fmtLimite(inv.limite_facturacion);
                  return (
                    <div key={inv.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`pago-${inv.id}`}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{inv.service_name}</p>
                          <p className="text-sm text-slate-500">
                            {fmtDate(inv.paid_at)} · {fmtMoney(inv.amount_total)}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                          }`}>
                            {inv.status === "paid" ? "Pagado" : inv.status}
                          </span>
                          {inv.has_cfdi ? (
                            <span className="flex items-center gap-1.5">
                              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Facturado</span>
                              {inv.pdf_url && (
                                <button className={btnOutline} onClick={() => handleDownload(inv.pdf_url, "pdf", inv)}>PDF</button>
                              )}
                              {inv.xml_url && (
                                <button className={btnOutline} onClick={() => handleDownload(inv.xml_url, "xml", inv)}>XML</button>
                              )}
                            </span>
                          ) : pendienteDeFacturar && inv.puede_facturar ? (
                            <button
                              disabled={requesting === inv.id}
                              onClick={() => handleRequestInvoice(inv.id)}
                              className="rounded-lg bg-brand-electric px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                              data-testid={`pedir-factura-${inv.id}`}
                            >
                              {requesting === inv.id ? "Generando…" : "Solicitar factura"}
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {/* La rama vencida se PINTA, no se calla. */}
                      {pendienteDeFacturar && limite && (
                        <p className="mt-2 text-sm text-slate-500">
                          {inv.puede_facturar
                            ? `Puedes facturar este pago hasta el ${limite}.`
                            : `El plazo para facturar este pago terminó el ${limite}.`}
                        </p>
                      )}

                      {result?.success && (
                        <div className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                          ✓ Factura generada exitosamente. Descarga desde la pestaña Mis facturas.
                          {result.warning && <p className="mt-1 text-xs text-amber-600">{result.warning}</p>}
                        </div>
                      )}
                      {result?.error && (
                        <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                          {result.error}
                          {result.canReload && (
                            <button type="button" onClick={() => window.location.reload()}
                              className="ml-2 font-medium underline">
                              Recargar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === "facturas" && (
            <div className="space-y-3">
              {withCfdi.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
                  <p className="text-slate-500">Aún no tienes facturas emitidas.</p>
                  <p className="mt-1 text-sm text-slate-400">
                    Puedes solicitar tu factura desde la pestaña{" "}
                    <button onClick={() => setActiveTab("pagos")} className="font-medium text-brand-electric hover:underline">
                      Mis pagos
                    </button>.
                  </p>
                </div>
              ) : (
                withCfdi.map((inv) => (
                  <div key={inv.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{inv.service_name}</p>
                        <p className="text-sm text-slate-500">
                          {fmtDate(inv.paid_at)} · {fmtMoney(inv.amount_total)}
                        </p>
                        {inv.cfdi_uuid && (
                          <p className="mt-1 font-mono text-xs text-slate-400 break-all">UUID: {inv.cfdi_uuid}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {inv.pdf_url && (
                          <button className={btnOutline} onClick={() => handleDownload(inv.pdf_url, "pdf", inv)}>Descargar PDF</button>
                        )}
                        {inv.xml_url && (
                          <button className={btnOutline} onClick={() => handleDownload(inv.xml_url, "xml", inv)}>Descargar XML</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {fiscalModal && (
        <DatosFiscalesModal
          inicial={fiscalModal.inicial}
          onSaved={onFiscalSaved}
          onCancel={() => {
            setFiscalModal(null);
            setPendingInvoiceId(null);
          }}
        />
      )}
    </div>
  );
}
